
import Vue from 'vue';
import Spinner from '@/core/components/Spinner.vue';
import MainButton from '@/core/components/MainButton.vue';
import { msalInstance, loginRequest } from '@/msal.config';
import { MsLoginRequest, User } from '@/models/auth';
import { AuthenticationResult } from '@azure/msal-browser';
import i18next from 'i18next';
import * as MsService from '@/services/ms.service';
import * as TelliqService from '@/services/telliq.service';
import { emitter, LANGUAGES } from '@/utils/constants';
import { MUTATIONS } from '@/store/mutations';

export default Vue.extend({
  data() {
    return {
      loading: false,
      valid: false,
      show_icon: false,
      username: '',
      password: '',
      rules: {
        username: [(v: string) => !!v || i18next.t('core.rules.required')],
        password: [(v: string) => !!v || i18next.t('core.rules.required')],
      },
    };
  },

  components: {
    Spinner,
    MainButton,
  },

  async created() {
    this.$store.commit(MUTATIONS.SET_HEADER, { title: 'Poolcar', subtitle: '' });
    const authenticationResult = await msalInstance.handleRedirectPromise();
    if (authenticationResult) {
      await this.microsoftAuthenticate(authenticationResult);
    }
  },

  methods: {
    async login() {
      if (this.valid) {
        this.loading = true;
        try {
          const response = await TelliqService.login(this.username, this.password);
          this.$store.commit(MUTATIONS.SET_API_KEY, response.guid);
          const user: User = {
            firstName: response.firstName,
            id: response.id,
            lastName: response.lastName,
            login: response.login,
          };
          this.$store.commit(MUTATIONS.SET_USER, user);

          this.redirectAfterLogin();
        } catch (e) {
          emitter.emit('notify', {
            text: this.$t('auth.wrong_username_or_password'),
            color: 'error',
          });
        }
        this.loading = false;
      }
    },

    getUserLanguage(userLanguage?: string): string | undefined {
      return LANGUAGES.find((lang) => lang.codes.some((langCode) => userLanguage?.match(langCode)))?.value;
    },

    async microsoftAuthenticate(authenticationResult: AuthenticationResult): Promise<void> {
      try {
        this.loading = true;
        const user_info = await MsService.getUserInfo(authenticationResult.accessToken);

        const userProfile: MsLoginRequest = {
          DisplayName: user_info.displayName,
          Login: user_info.mail ?? user_info.userPrincipalName,
          UserPrincipalName: user_info.userPrincipalName,
          Username: user_info.mail ?? user_info.userPrincipalName,
          FirstName: user_info.givenName,
          LastName: user_info.surname,
          preferredLanguage: this.getUserLanguage(user_info.preferredLanguage),
        };
        const response = await TelliqService.msLogin(authenticationResult.tenantId, userProfile);
        this.$store.commit(MUTATIONS.SET_API_KEY, response.Token);
        const user: User = {
          firstName: response.FirstName,
          id: response.UserId,
          lastName: response.LastName,
          login: response.Login,
        };
        this.$store.commit(MUTATIONS.SET_USER, user);

        this.redirectAfterLogin();
      } catch (e) {
        msalInstance.logoutRedirect({ account: msalInstance.getAllAccounts()[0] });
      }
      this.loading = false;
    },

    isAuthenticated(): boolean {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        return true;
      }
      return false;
    },

    async microsoftSignIn() {
      try {
        if (!this.isAuthenticated()) {
          await msalInstance.loginRedirect(loginRequest);
        } else {
          const account = msalInstance.getAllAccounts()[0];
          const authenticationResponse = await msalInstance.acquireTokenSilent({
            scopes: ['user.read'],
            account,
          });
          await this.microsoftAuthenticate(authenticationResponse);
        }
      } catch (e) {
        emitter.emit('notify', {
          text: this.$t('core.errors.something_went_wrong'),
          color: 'error',
        });
        msalInstance.logoutRedirect({ account: msalInstance.getAllAccounts()[0] });
      }
    },

    redirectAfterLogin(): void {
      const url = this.$store.state.lastUrl || '/';
      this.$router.push(url);
    },
  },
});
